import { useEffect, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, Building2, CreditCard, Loader2, QrCode, ShieldCheck, X } from "lucide-react";
import { formatPhp } from "../lib/money";
import { useI18n } from "../lib/i18n";
import "./MobileWalletCashSheet.css";

export type WalletPayoutDestination = { bankCode: string; accountNumber: string; receiverName: string };
type CashMethod = "qrph" | "card";
type WithdrawalReview = { amount: number; destination: WalletPayoutDestination; bankName: string };
type MobileWalletCashSheetProps = {
  mode: "in" | "out";
  amount: string;
  onAmountChange: (amount: string) => void;
  destination: WalletPayoutDestination;
  onDestinationChange: (destination: WalletPayoutDestination) => void;
  channels: readonly { code: string; name: string }[];
  available: number;
  limits: { min: number; max: number };
  provider: string;
  isBusy: boolean;
  error: string | null;
  onClose: () => void;
  onCashIn: (amount: number, method: CashMethod) => Promise<void>;
  onCashOut: (amount: number, destination: WalletPayoutDestination) => Promise<void>;
};

/** Mobile presentation only: Wallet owns provider calls, permissions, and settlement.
 * The native modal contains focus and makes the wallet behind the sheet inert. */
export default function MobileWalletCashSheet(props: MobileWalletCashSheetProps) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const [method, setMethod] = useState<CashMethod>("qrph");
  const [review, setReview] = useState<WithdrawalReview | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const isCashOut = props.mode === "out";
  const title = review ? t("wallet.cash.review") : t(isCashOut ? "wallet.cashOut" : "wallet.cashIn");

  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog?.showModal();
    return () => {
      dialog?.close();
      document.body.style.overflow = previousOverflow;
      if (trigger?.isConnected) trigger.focus();
    };
  }, []);

  useEffect(() => {
    if (validationError || props.error) errorRef.current?.scrollIntoView({ block: "nearest" });
  }, [validationError, props.error]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (props.isBusy) return;
    if (review) {
      void props.onCashOut(review.amount, review.destination);
      return;
    }
    const amount = Number(props.amount);
    // [wallet.cash-movement](../../../docs/product/wallet-and-credit.md#wallet-cash-movement): whole-peso amounts use API limits; cash-out also respects availability.
    if (!props.amount.trim() || !Number.isInteger(amount) || amount < props.limits.min || amount > props.limits.max) {
      setValidationError(t("wallet.cash.amountError"));
      return;
    }
    if (!isCashOut) {
      void props.onCashIn(amount, method);
      return;
    }
    if (amount > props.available) { setValidationError(t("wallet.cash.balanceError")); return; }
    const channel = props.channels.find((channel) => channel.code === props.destination.bankCode);
    if (!channel) { setValidationError(t("wallet.cash.destinationError")); return; }
    const destination = { ...props.destination, accountNumber: props.destination.accountNumber.trim(), receiverName: props.destination.receiverName.trim() };
    if (!destination.accountNumber || !destination.receiverName) { setValidationError(t("wallet.cash.receiverError")); return; }
    // Snapshot the reviewed payload, so a late saved-account response cannot change
    // the recipient between review and confirmation. Wallet still validates the channel.
    setValidationError(null);
    setReview({ amount, destination, bankName: channel.name });
  }

  return createPortal(
    <dialog ref={dialogRef} className="wallet-cash-sheet" aria-labelledby="wallet-cash-title"
      onCancel={(event) => { event.preventDefault(); if (!props.isBusy) props.onClose(); }}
      onClick={(event) => { if (event.target === event.currentTarget && !props.isBusy) props.onClose(); }}>
      <form onSubmit={submit} noValidate>
        <div className="wallet-sheet-handle" aria-hidden="true" />
        <header className="wallet-sheet-header">
          <div><h2 id="wallet-cash-title">{title}</h2><p>{t(review ? "wallet.cash.fromWallet" : isCashOut ? "wallet.cash.outSubtitle" : "wallet.cash.inSubtitle")}{!isCashOut && !review && ` ${props.provider}`}</p></div>
          <button type="button" className="wallet-sheet-close" disabled={props.isBusy} onClick={props.onClose} aria-label={t("wallet.cash.close")}><X aria-hidden="true" /></button>
        </header>
        <div className="wallet-sheet-body">
          {review ? <>
            <p className="wallet-sheet-review-amount">{formatPhp(review.amount)}</p>
            <dl className="wallet-sheet-review-details">
              <div><dt>{t("wallet.cash.destination")}</dt><dd>{review.bankName}</dd></div>
              <div><dt>{t("wallet.cash.account")}</dt><dd>•••• {review.destination.accountNumber.slice(-4)}</dd></div>
              <div><dt>{t("wallet.cash.receiver")}</dt><dd>{review.destination.receiverName}</dd></div>
            </dl>
            <p className="wallet-sheet-note">{t("wallet.cash.reservedNotice")}</p>
            <button type="button" className="wallet-sheet-edit" disabled={props.isBusy} onClick={() => setReview(null)}>{t("wallet.cash.edit")}</button>
          </> : <>
            {isCashOut && <div className="wallet-sheet-available"><span>{t("wallet.cash.available")}</span><strong>{formatPhp(props.available)}</strong></div>}
            <label className="wallet-sheet-label" htmlFor="wallet-cash-amount">{t(isCashOut ? "wallet.cash.amountOut" : "wallet.cash.amountIn")}</label>
            <div className="wallet-sheet-amount-field"><span aria-hidden="true">₱</span><input id="wallet-cash-amount" type="number" inputMode="numeric" min={props.limits.min} max={props.limits.max} step={1} disabled={props.isBusy}
              placeholder="0.00" value={props.amount} aria-describedby="wallet-cash-amount-hint" onChange={(event) => { setValidationError(null); props.onAmountChange(event.target.value); }} /></div>
            <p id="wallet-cash-amount-hint" className="wallet-sheet-hint">{t("wallet.cash.min")} {formatPhp(props.limits.min)} · {t("wallet.cash.max")} {formatPhp(props.limits.max)}</p>
            <div className="wallet-sheet-presets" aria-label={t("wallet.cash.suggested")}>{[1000, 5000, 10000].filter((amount) => amount >= props.limits.min && amount <= props.limits.max).map((amount) =>
              <button key={amount} type="button" disabled={props.isBusy || (isCashOut && amount > props.available)} aria-pressed={Number(props.amount) === amount} onClick={() => { setValidationError(null); props.onAmountChange(String(amount)); }}>{formatPhp(amount).replace(/\.00$/, "")}</button>
            )}</div>
            {isCashOut ? <>
              <h3 className="wallet-sheet-destination-title"><Building2 aria-hidden="true" />{t("wallet.cash.transferTo")}</h3>
              <div className="wallet-sheet-recipient-fields">
                <label htmlFor="wallet-cash-bank">{t("wallet.cash.bank")}<select id="wallet-cash-bank" disabled={props.isBusy} value={props.destination.bankCode} onChange={(event) => { setValidationError(null); props.onDestinationChange({ ...props.destination, bankCode: event.target.value }); }}>
                  <option value="">{t("wallet.cash.chooseBank")}</option>{props.channels.map((channel) => <option key={channel.code} value={channel.code}>{channel.name}</option>)}
                </select></label>
                <label htmlFor="wallet-cash-account">{t("wallet.cash.account")}<input id="wallet-cash-account" type="text" inputMode="numeric" autoComplete="off" maxLength={64} disabled={props.isBusy} value={props.destination.accountNumber} onChange={(event) => { setValidationError(null); props.onDestinationChange({ ...props.destination, accountNumber: event.target.value }); }} /></label>
                <label htmlFor="wallet-cash-receiver">{t("wallet.cash.receiver")}<input id="wallet-cash-receiver" type="text" autoComplete="off" maxLength={120} disabled={props.isBusy} value={props.destination.receiverName} onChange={(event) => { setValidationError(null); props.onDestinationChange({ ...props.destination, receiverName: event.target.value }); }} /></label>
              </div>
            </> : <>
              <h3 className="wallet-sheet-method-title">{t("wallet.cash.paymentMethod")}</h3>
              <div className="wallet-sheet-methods">{(["qrph", "card"] as const).map((option) => <button key={option} type="button" disabled={props.isBusy} aria-pressed={method === option} onClick={() => setMethod(option)}>
                {option === "qrph" ? <QrCode aria-hidden="true" /> : <CreditCard aria-hidden="true" />}{option === "qrph" ? "QRPH" : t("wallet.cash.card")}
              </button>)}</div>
              <div className="wallet-sheet-selected-method"><strong>{t(method === "qrph" ? "wallet.cash.qrTitle" : "wallet.cash.cardTitle")}</strong><p>{t(method === "qrph" ? "wallet.cash.qrDescription" : "wallet.cash.cardDescription")}</p>
                <div className="wallet-sheet-logos">{method === "qrph" ? [["gcash.png", "GCash"], ["maya.webp", "Maya"], ["bdopay.jpg", "BDO Pay"], ["bpi.webp", "BPI"]].map(([file, name]) => <img key={file} src={`/pay/${file}`} alt={name} />)
                  : <img className="wallet-sheet-card-logos" src="/pay/cards.png" alt="Visa, Mastercard, American Express, Discover" />}</div>
              </div>
            </>}
          </>}
          {(validationError || props.error) && <p ref={errorRef} className="wallet-sheet-error" role="alert">{validationError || props.error}</p>}
        </div>
        <footer className="wallet-sheet-footer">
          <button type="submit" className="wallet-sheet-primary" disabled={props.isBusy}>{props.isBusy ? <><Loader2 className="animate-spin" aria-hidden="true" />{t("wallet.cash.processing")}</> : <>{t(review ? "wallet.cash.withdraw" : isCashOut ? "wallet.cash.review" : method === "qrph" ? "wallet.cash.continueQR" : "wallet.cash.continueCard")}<ArrowRight aria-hidden="true" /></>}</button>
          <p><ShieldCheck aria-hidden="true" />{t("wallet.cash.poweredBy")} {props.provider}</p>
        </footer>
      </form>
    </dialog>, document.body,
  );
}
